/**
 * The alarm sound.
 *
 * Kept as thin as possible — all the "when" rules live in `alarm-model.ts`, which is testable. This
 * file only knows how to make a noise.
 *
 * Synthesised with an oscillator rather than shipping an audio file: no asset to host or cache, it
 * works with the tab offline, and the pattern can be tuned without a round trip through a sound
 * editor.
 *
 * Browsers require a user gesture before audio may play. `arm()` must therefore be called from a
 * click handler; until it succeeds, `armed` is false and the UI says so rather than pretending the
 * alarm is active.
 */

/**
 * A rising major triad — C6, E6, G6 — struck like a bell rather than held like a siren.
 *
 * The first version was a square wave holding two tones, which read as an industrial alarm: square
 * waves are all odd harmonics, and a sustained tone is what danger sounds like. What actually cuts
 * through a kitchen is a sharp ATTACK, not a harsh timbre, so this keeps the sudden onset and
 * replaces the buzz with a triangle fundamental plus a quiet octave for shimmer, decaying away like
 * a struck chime.
 *
 * Consonant on purpose. It repeats until somebody acknowledges it, and a dissonant interval heard
 * forty times in a lunch rush makes people resent the tool.
 */
const NOTES: { atMs: number; hz: number }[] = [
  { atMs: 0, hz: 1046.5 },
  { atMs: 130, hz: 1318.5 },
  { atMs: 260, hz: 1568.0 }
];
/** Long enough to ring out, short enough that the next note is a chord rather than a smear. */
const DECAY_S = 0.55;
const REPEAT_MS = 3000;

/**
 * How many times the chime repeats before falling silent.
 *
 * It used to loop until somebody acknowledged it. Three is enough to be noticed and not enough to become
 * the thing people resent — a noise that will not stop teaches staff to silence it pre-emptively, which
 * loses the next order. The alarm BAR stays until acknowledged, so the order is still visibly waiting
 * after the sound has stopped.
 */
const MAX_CYCLES = 3;

export interface Alarm {
  /** Call from a user gesture. Resolves true when audio is usable in this tab. */
  arm(): Promise<boolean>;
  readonly armed: boolean;
  start(): void;
  stop(): void;
  /** One cycle, to prove out loud that the alarm works when it is switched on. */
  test(): void;
  dispose(): void;
}

type Ctor = typeof AudioContext;

export function createAlarm(ctor: Ctor | undefined = globalThis.AudioContext): Alarm {
  let ctx: AudioContext | undefined;
  let timer: ReturnType<typeof setInterval> | undefined;

  /** One struck note: fundamental plus a quiet octave, with a bell-like decay. */
  const strike = (audio: AudioContext, at: number, hz: number, peak: number, type: OscillatorType) => {
    const osc = audio.createOscillator();
    const gain = audio.createGain();
    osc.type = type;
    osc.frequency.value = hz;
    gain.gain.setValueAtTime(0, at);
    // 6ms attack: fast enough to sound struck, slow enough not to click.
    gain.gain.linearRampToValueAtTime(peak, at + 0.006);
    // Exponential, because that is how a real resonating body decays — a linear fade sounds
    // synthetic. It cannot reach zero, hence the small floor.
    gain.gain.exponentialRampToValueAtTime(0.0001, at + DECAY_S);
    osc.connect(gain).connect(audio.destination);
    osc.start(at);
    osc.stop(at + DECAY_S);
  };

  const cycle = () => {
    if (!ctx || ctx.state !== "running") return;
    const now = ctx.currentTime;
    for (const n of NOTES) {
      const at = now + n.atMs / 1000;
      strike(ctx, at, n.hz, 0.22, "triangle");
      // Octave above, quiet: gives it the ring of a chime instead of a plain tone.
      strike(ctx, at, n.hz * 2, 0.05, "sine");
    }
  };

  return {
    get armed() {
      return ctx?.state === "running";
    },
    async arm() {
      if (!ctor) return false;
      try {
        ctx ??= new ctor();
        // Chrome creates the context suspended when there has been no gesture yet.
        if (ctx.state === "suspended") await ctx.resume();
        return ctx.state === "running";
      } catch {
        return false;
      }
    },
    start() {
      if (!ctx) return;
      // Always restart rather than returning early when a timer exists: a second order arriving mid-chime
      // is a new event and deserves its own three, not the tail of the previous one.
      if (timer) clearInterval(timer);
      let played = 1;
      cycle();
      timer = setInterval(() => {
        if (played >= MAX_CYCLES) {
          if (timer) clearInterval(timer);
          timer = undefined;
          return;
        }
        played++;
        cycle();
      }, REPEAT_MS);
    },
    stop() {
      if (timer) clearInterval(timer);
      timer = undefined;
    },
    test() {
      cycle();
    },
    dispose() {
      if (timer) clearInterval(timer);
      timer = undefined;
      void ctx?.close();
      ctx = undefined;
    }
  };
}
